/**
 * Oracle knowledge base — static F1 rules, regulations, and glossary facts.
 * -------------------------------------------------------------------------
 * This file is designed for the future on-device/local-model path. Entries are
 * concise original summaries of official F1/FIA material, not copied rule text.
 *
 * Use this KB for static or slow-changing questions. Do not use it as the source
 * of truth for live race state, current standings, telemetry, penalties in a
 * specific session, or historical result lookups that should come from APIs.
 */

export const oracleKnowledgeMeta = {
  id: 'oracle-static-f1-kb',
  version: '0.1.0',
  lastReviewed: '2026-06-09',
  intendedUse: [
    'Local/offline answers for F1 concepts, rules, and glossary questions.',
    'Intent classification before deciding whether backend race data is required.',
    'Grounding context for a small on-device model so it does not rely on memory.',
  ],
  answerBoundary: [
    'Answer from this KB only for static rules, regulations, formats, and terms.',
    'Route live race, current standings, race result, penalty, weather, tyre age, sector, or telemetry questions to backend data tools.',
    'If an answer depends on the active season regulations, mention that FIA regulations can change and cite the reviewed season context.',
  ],
};

export const oracleKnowledgeSources = {
  f1WhatIsF1: {
    title: 'Formula 1: What is F1?',
    publisher: 'Formula 1',
    url: 'https://www.formula1.com/en/page/what-is-f1',
  },
  f1Glossary: {
    title: 'F1 Glossary',
    publisher: 'Formula 1',
    url: 'https://www.formula1.com/en/page/f1-glossary',
  },
  f1Regulations2026: {
    title: '2026 Formula 1 Regulations',
    publisher: 'Formula 1',
    url: 'https://www.formula1.com/en/page/2026-f1-regulations',
  },
  fiaRegulations: {
    title: 'FIA Formula One Regulations',
    publisher: 'FIA',
    url: 'https://www.fia.com/regulation/category/110',
  },
};

export const oracleKnowledgeBase = [
  {
    id: 'f1-overview',
    title: 'What Formula 1 Is',
    category: 'f1-basics',
    sourceRefs: ['f1WhatIsF1', 'fiaRegulations'],
    tags: ['formula 1', 'f1', 'grand prix', 'teams', 'drivers', 'championship'],
    queries: ['what is f1', 'what is formula 1', 'how does formula 1 work'],
    answer: {
      summary: 'Formula 1 is the top FIA-regulated single-seater racing championship. Teams design and operate cars, drivers race across a calendar of Grands Prix, and points decide the drivers and constructors championships.',
      keyPoints: [
        'Each race weekend is a Grand Prix event with practice, qualifying, and race sessions, with Sprint weekends using a different format.',
        'Drivers score points for themselves and their teams; teams compete for the constructors championship.',
        'The FIA writes and enforces the sporting, technical, and financial regulations. Formula 1 promotes and operates the championship commercially.',
      ],
      routeLiveDataWhen: ['current calendar', 'current standings', 'driver lineup', 'race result', 'specific session status'],
    },
  },
  {
    id: 'weekend-standard-format',
    title: 'Standard Grand Prix Weekend Format',
    category: 'weekend-format',
    sourceRefs: ['f1WhatIsF1', 'fiaRegulations'],
    tags: ['weekend', 'practice', 'qualifying', 'race', 'fp1', 'fp2', 'fp3'],
    queries: ['how does an f1 weekend work', 'what happens on friday', 'what is fp1', 'what is fp2', 'what is fp3'],
    answer: {
      summary: 'A standard F1 weekend usually builds from practice sessions into qualifying and then the Grand Prix race.',
      keyPoints: [
        'Practice lets teams tune the car, assess tyres, run race simulations, and prepare qualifying laps.',
        'Qualifying decides the starting order for the Grand Prix unless penalties or regulation-specific exceptions apply.',
        'The race awards championship points and is the main competitive session of the weekend.',
      ],
      caveats: ['Exact timings and session order can vary by event, local schedule, weather, and Sprint format.'],
    },
  },
  {
    id: 'weekend-sprint-format',
    title: 'Sprint Weekend Format',
    category: 'weekend-format',
    sourceRefs: ['f1WhatIsF1', 'fiaRegulations'],
    tags: ['sprint', 'sprint qualifying', 'sprint shootout', 'sprint weekend'],
    queries: ['what is a sprint weekend', 'how does f1 sprint work', 'what is sprint qualifying'],
    answer: {
      summary: 'A Sprint weekend adds a short points-paying race to the event and separates the Sprint grid from the Grand Prix grid.',
      keyPoints: [
        'Recent Sprint formats use one practice session, Sprint Qualifying for the Sprint grid, the Sprint race, Grand Prix qualifying, and the Grand Prix.',
        'The Sprint is shorter than a Grand Prix and awards fewer points.',
        'Grand Prix qualifying still sets the grid for the main race, subject to penalties and current regulations.',
      ],
      caveats: ['Sprint rules have changed several times, so answer season-specific questions with the active FIA regulations.'],
    },
  },
  {
    id: 'qualifying-knockout',
    title: 'Qualifying Knockout Format',
    category: 'sporting-rules',
    sourceRefs: ['f1WhatIsF1', 'fiaRegulations'],
    tags: ['qualifying', 'q1', 'q2', 'q3', 'grid', 'pole position'],
    queries: ['how does qualifying work', 'what is q1 q2 q3', 'what is pole position'],
    answer: {
      summary: 'F1 qualifying is a knockout session. Drivers set lap times, the slowest are eliminated after early segments, and the final segment decides the front of the grid and pole position.',
      keyPoints: [
        'Q1 includes the full field; the slowest cars are eliminated.',
        'Q2 narrows the field again.',
        'Q3 decides the top starting positions, with the fastest driver in Q3 taking pole before penalties are applied.',
      ],
      caveats: ['The exact number of eliminated cars depends on the grid size and current FIA rules.'],
    },
  },
  {
    id: 'points-grand-prix',
    title: 'Grand Prix Points System',
    category: 'championship',
    sourceRefs: ['f1WhatIsF1', 'fiaRegulations'],
    tags: ['points', 'scoring', 'championship', 'drivers championship', 'constructors championship'],
    queries: ['how many points for a win', 'how do f1 points work', 'grand prix points system'],
    answer: {
      summary: 'In the current standard Grand Prix points structure, the top ten finishers score points: 25, 18, 15, 12, 10, 8, 6, 4, 2, and 1.',
      keyPoints: [
        'The race winner scores 25 points.',
        'Drivers from P2 to P10 score progressively fewer points.',
        'The same driver points also count toward that team in the constructors championship.',
      ],
      caveats: ['Abandoned or shortened races can have special point rules. Always use backend data for a specific race result.'],
    },
  },
  {
    id: 'points-sprint',
    title: 'Sprint Points System',
    category: 'championship',
    sourceRefs: ['f1WhatIsF1', 'fiaRegulations'],
    tags: ['sprint points', 'sprint race', 'scoring'],
    queries: ['how many points for sprint', 'sprint points', 'does sprint award points'],
    answer: {
      summary: 'The Sprint awards points to the top eight finishers: 8, 7, 6, 5, 4, 3, 2, and 1.',
      keyPoints: [
        'Sprint points count toward both drivers and constructors championships.',
        'Sprint points are separate from the Grand Prix points later in the weekend.',
        'The Sprint is shorter than the Grand Prix and normally does not require a pit stop.',
      ],
      caveats: ['Sprint formats have changed over time; use season context for older years.'],
    },
  },
  {
    id: 'fastest-lap-point',
    title: 'Fastest Lap Point Context',
    category: 'championship',
    sourceRefs: ['fiaRegulations'],
    tags: ['fastest lap', 'point', 'scoring', 'historical rules'],
    queries: ['does fastest lap get a point', 'fastest lap point', 'why no fastest lap point'],
    answer: {
      summary: 'The fastest-lap bonus point was used in recent past seasons but is not part of the current 2025-and-later F1 points system.',
      keyPoints: [
        'During the bonus-point era, the fastest lap point only applied if the driver finished in the points-paying positions.',
        'For current seasons, use the race classification and current FIA scoring rules instead of assuming a fastest-lap point exists.',
      ],
      caveats: ['For historical seasons, route to backend results data so the correct year-specific rule is applied.'],
    },
  },
  {
    id: 'championships-drivers-constructors',
    title: 'Drivers and Constructors Championships',
    category: 'championship',
    sourceRefs: ['f1WhatIsF1', 'fiaRegulations'],
    tags: ['drivers championship', 'constructors championship', 'team points'],
    queries: ['what is the constructors championship', 'what is the drivers championship', 'how do teams score points'],
    answer: {
      summary: 'F1 has two main championships. Drivers compete individually, while teams compete through the combined points scored by their entered cars.',
      keyPoints: [
        'The drivers championship ranks drivers by points across the season.',
        'The constructors championship ranks teams by the points scored by their drivers.',
        'A driver change does not erase points already scored for the team.',
      ],
      routeLiveDataWhen: ['current standings', 'points gap', 'clinching scenarios', 'specific season totals'],
    },
  },
  {
    id: 'parc-ferme',
    title: 'Parc Ferme',
    category: 'sporting-rules',
    sourceRefs: ['f1Glossary', 'fiaRegulations'],
    tags: ['parc ferme', 'setup', 'scrutineering', 'qualifying'],
    queries: ['what is parc ferme', 'can teams change setup after qualifying', 'why parc ferme exists'],
    answer: {
      summary: 'Parc ferme is the restricted condition where teams cannot freely change car setup after a defined point in the weekend.',
      keyPoints: [
        'The rule stops teams from qualifying with one car specification and racing with another unrestricted setup.',
        'Teams may still perform allowed work for safety, reliability, damage repair, and FIA-approved operations.',
        'Breaking parc ferme rules can lead to pit lane starts or penalties.',
      ],
      caveats: ['The exact start point and allowed work depend on the weekend format and current FIA regulations.'],
    },
  },
  {
    id: 'tyres-compounds',
    title: 'F1 Tyre Compounds',
    category: 'tyres-strategy',
    sourceRefs: ['f1Glossary', 'fiaRegulations'],
    tags: ['tyres', 'tires', 'soft', 'medium', 'hard', 'intermediate', 'wet'],
    queries: ['what are f1 tyre compounds', 'soft medium hard tyres', 'what are intermediates', 'what are wet tyres'],
    answer: {
      summary: 'F1 uses slick dry tyres plus wet-weather tyres. Soft, medium, and hard describe relative grip and durability choices for a race weekend.',
      keyPoints: [
        'Soft tyres usually offer more grip but wear faster.',
        'Hard tyres usually last longer but may be slower or harder to warm up.',
        'Intermediate and full wet tyres are grooved for damp or wet conditions.',
      ],
      routeLiveDataWhen: ['current tyre age', 'driver stint compound', 'race strategy for a specific session'],
    },
  },
  {
    id: 'tyre-race-rules',
    title: 'Dry Race Tyre Rule',
    category: 'tyres-strategy',
    sourceRefs: ['fiaRegulations'],
    tags: ['mandatory tyre compound', 'two compounds', 'pit stop', 'dry race'],
    queries: ['do drivers have to pit', 'must f1 drivers use two compounds', 'why pit in a dry race'],
    answer: {
      summary: 'In a dry Grand Prix, drivers normally must use at least two different dry tyre specifications, which usually forces at least one pit stop.',
      keyPoints: [
        'The rule applies to dry-weather slick compounds.',
        'If wet-weather tyres are used, the mandatory dry-compound requirement can change under FIA rules.',
        'Sprint races generally do not carry the same mandatory pit-stop requirement as a Grand Prix.',
      ],
      caveats: ['Always check the current sporting regulations for edge cases such as red flags, wet races, and special event instructions.'],
    },
  },
  {
    id: 'strategy-undercut-overcut',
    title: 'Undercut and Overcut',
    category: 'tyres-strategy',
    sourceRefs: ['f1Glossary'],
    tags: ['undercut', 'overcut', 'pit strategy', 'strategy'],
    queries: ['what is an undercut', 'what is an overcut', 'how does pit strategy work'],
    answer: {
      summary: 'An undercut means pitting before a rival to use fresher tyres to gain time. An overcut means staying out longer and trying to gain time before pitting later.',
      keyPoints: [
        'Undercuts work best when fresh tyres are immediately much faster and the out-lap is clear.',
        'Overcuts work when the old tyres still perform well, the rival struggles on cold tyres, or track position/traffic favors staying out.',
        'Safety cars, virtual safety cars, pit lane loss, tyre warm-up, and traffic can flip the best strategy.',
      ],
      routeLiveDataWhen: ['specific race strategy', 'current tyre age', 'gap needed to pit and stay ahead'],
    },
  },
  {
    id: 'pit-lane-pit-stops',
    title: 'Pit Lane and Pit Stops',
    category: 'sporting-rules',
    sourceRefs: ['f1Glossary', 'fiaRegulations'],
    tags: ['pit stop', 'pit lane', 'pit speed limit', 'pit crew'],
    queries: ['how do pit stops work', 'what is pit lane speed limit', 'why are pit stops so fast'],
    answer: {
      summary: 'Pit stops let teams change tyres, serve some penalties, and fix limited damage. The pit lane is speed-limited and heavily regulated for safety.',
      keyPoints: [
        'A normal tyre stop changes all four tyres with a large coordinated crew.',
        'Drivers must respect the pit lane speed limit from entry to exit timing lines.',
        'Unsafe releases, speeding, or improper penalty service can trigger penalties.',
      ],
      routeLiveDataWhen: ['specific pit stop time', 'pit lane loss', 'current race pit history'],
    },
  },
  {
    id: 'drs',
    title: 'DRS',
    category: 'car-technology',
    sourceRefs: ['f1Glossary', 'fiaRegulations'],
    tags: ['drs', 'drag reduction system', 'rear wing', 'overtaking'],
    queries: ['what is drs', 'how does drs work', 'when can drivers use drs'],
    answer: {
      summary: 'DRS is the Drag Reduction System. It opens a rear-wing flap in designated zones to reduce drag and help a following car overtake.',
      keyPoints: [
        'In races, DRS is normally enabled only in defined zones and when a driver is close enough to the car ahead at a detection point.',
        'Race Control can disable DRS for safety, weather, yellow flags, or other conditions.',
        'DRS rules differ between race, qualifying, and future 2026-era active-aero concepts.',
      ],
      caveats: ['For 2026 rules, use active-aero and manual override entries rather than assuming old DRS behavior.'],
    },
  },
  {
    id: 'aero-dirty-air-slipstream',
    title: 'Dirty Air and Slipstream',
    category: 'car-technology',
    sourceRefs: ['f1Glossary'],
    tags: ['dirty air', 'slipstream', 'tow', 'aerodynamics', 'downforce'],
    queries: ['what is dirty air', 'what is slipstream', 'what is a tow', 'why is following hard in f1'],
    answer: {
      summary: 'Dirty air is turbulent airflow behind a car that can reduce downforce for the following car. A slipstream or tow is reduced drag on a straight behind another car.',
      keyPoints: [
        'Dirty air hurts cornering because the following car has less clean airflow over its aerodynamic surfaces.',
        'A slipstream helps on straights because the following car punches through less air.',
        'The trade-off is why a car can gain on a straight but struggle through corners behind another car.',
      ],
    },
  },
  {
    id: 'ers-power-unit',
    title: 'ERS and Hybrid Power Units',
    category: 'car-technology',
    sourceRefs: ['f1Glossary', 'f1Regulations2026', 'fiaRegulations'],
    tags: ['ers', 'mguk', 'mgu-k', 'battery', 'hybrid', 'power unit'],
    queries: ['what is ers', 'what is mgu-k', 'how do f1 hybrid engines work', 'what is power unit'],
    answer: {
      summary: 'F1 power units combine a turbocharged combustion engine with hybrid electrical energy recovery and deployment systems.',
      keyPoints: [
        'ERS stores recovered energy and deploys it for extra power through the hybrid system.',
        'MGU-K refers to the motor-generator unit connected to kinetic energy recovery and deployment.',
        'Energy management is a major part of lap time, overtaking, and race strategy.',
      ],
      caveats: ['Technical details differ between rules eras, especially with the 2026 power-unit reset.'],
    },
  },
  {
    id: 'flags-overview',
    title: 'Racing Flags',
    category: 'sporting-rules',
    sourceRefs: ['f1Glossary', 'fiaRegulations'],
    tags: ['flags', 'yellow flag', 'red flag', 'blue flag', 'green flag', 'black flag', 'chequered flag'],
    queries: ['what do f1 flags mean', 'yellow flag meaning', 'blue flag meaning', 'black flag meaning'],
    answer: {
      summary: 'Flags communicate track status and instructions from officials to drivers. They control safety, overtaking rules, and whether a session is active or stopped.',
      keyPoints: [
        'Green means normal racing conditions.',
        'Yellow means danger ahead; drivers must slow and be ready to change direction. Double yellow is more serious.',
        'Red stops the session. Blue warns a slower car that a faster car is approaching to lap it. Black-flag variants can require a driver to report to the pits or warn about conduct/mechanical issues.',
        'The chequered flag ends the session or race.',
      ],
      routeLiveDataWhen: ['current track status', 'specific race-control message', 'lap-specific flag period'],
    },
  },
  {
    id: 'safety-car-vsc',
    title: 'Safety Car and Virtual Safety Car',
    category: 'sporting-rules',
    sourceRefs: ['f1Glossary', 'fiaRegulations'],
    tags: ['safety car', 'virtual safety car', 'vsc', 'neutralization'],
    queries: ['what is safety car', 'what is virtual safety car', 'difference between safety car and vsc'],
    answer: {
      summary: 'The Safety Car and Virtual Safety Car neutralize a race for safety. Both slow the field, but they work differently.',
      keyPoints: [
        'Under Safety Car, drivers bunch behind the physical Safety Car and overtaking is heavily restricted.',
        'Under VSC, no physical Safety Car leads the pack; drivers must respect time/delta limits around the lap.',
        'Both can create major strategy changes because pitting costs less time when the field is slowed.',
      ],
      routeLiveDataWhen: ['current safety car period', 'who gained from a safety car', 'lap of deployment'],
    },
  },
  {
    id: 'red-flag',
    title: 'Red Flags',
    category: 'sporting-rules',
    sourceRefs: ['f1Glossary', 'fiaRegulations'],
    tags: ['red flag', 'race stopped', 'restart', 'suspension'],
    queries: ['what happens under red flag', 'why is race stopped', 'red flag rules'],
    answer: {
      summary: 'A red flag stops a session because conditions are unsafe or the track needs recovery/repair work.',
      keyPoints: [
        'Drivers normally return slowly to the pit lane or grid as instructed by Race Control.',
        'The session may restart if conditions allow, or it may end if it cannot continue within the rules.',
        'Tyre changes, repairs, and restart order are governed by the sporting regulations and Race Control instructions.',
      ],
      caveats: ['Red-flag restart rules are regulation-specific; use current FIA rules for exact edge cases.'],
    },
  },
  {
    id: 'race-control-stewards',
    title: 'Race Control and Stewards',
    category: 'sporting-rules',
    sourceRefs: ['fiaRegulations'],
    tags: ['race control', 'stewards', 'penalties', 'investigation', 'noted'],
    queries: ['what does race control do', 'what do stewards do', 'what does incident noted mean'],
    answer: {
      summary: 'Race Control manages the live running of sessions, while stewards judge incidents and apply penalties under FIA rules.',
      keyPoints: [
        'Race Control handles operational decisions such as Safety Car, red flags, DRS status, and session instructions.',
        'Stewards review incidents, evidence, driver statements, telemetry, video, and regulations.',
        'An incident being noted means officials are aware of it; investigated means stewards are formally reviewing it.',
      ],
      routeLiveDataWhen: ['specific incident', 'current penalty', 'stewards decision document'],
    },
  },
  {
    id: 'penalties-common',
    title: 'Common F1 Penalties',
    category: 'sporting-rules',
    sourceRefs: ['fiaRegulations'],
    tags: ['penalty', 'time penalty', 'drive through', 'stop go', 'grid penalty', 'penalty points'],
    queries: ['what penalties can drivers get', 'what is a five second penalty', 'what is a drive through penalty', 'what are penalty points'],
    answer: {
      summary: 'F1 penalties range from time penalties to grid penalties, drive-throughs, stop-go penalties, disqualification, reprimands, and licence penalty points.',
      keyPoints: [
        'Five- and ten-second time penalties can be served at a pit stop or added to race time, depending on timing and rules.',
        'Drive-through and stop-go penalties force the driver through the pit lane, with stop-go requiring a stationary stop.',
        'Grid penalties usually apply to future starting positions, often for component changes or sporting infringements.',
        'Penalty points attach to a driver licence and can trigger a race ban if enough are accumulated in the defined period.',
      ],
      routeLiveDataWhen: ['who got penalized', 'specific penalty reason', 'active penalty points count'],
    },
  },
  {
    id: 'track-limits',
    title: 'Track Limits',
    category: 'sporting-rules',
    sourceRefs: ['f1Glossary', 'fiaRegulations'],
    tags: ['track limits', 'white line', 'deleted lap', 'penalty'],
    queries: ['what are track limits', 'why was lap deleted', 'why track limits penalty'],
    answer: {
      summary: 'Track limits define the part of the circuit drivers are allowed to use. Running beyond the defined boundary can delete lap times or lead to race penalties.',
      keyPoints: [
        'The white lines normally define the track edge unless event notes specify otherwise.',
        'In qualifying or practice, a track-limits breach can delete that lap time and sometimes the next lap if an advantage carries over.',
        'In races, repeated breaches can lead to warnings and time penalties.',
      ],
      routeLiveDataWhen: ['specific driver warning count', 'deleted lap', 'race-specific stewarding decision'],
    },
  },
  {
    id: 'blue-flags',
    title: 'Blue Flags',
    category: 'sporting-rules',
    sourceRefs: ['f1Glossary', 'fiaRegulations'],
    tags: ['blue flag', 'lapped car', 'lapping'],
    queries: ['what is a blue flag', 'do lapped cars have to move over', 'why blue flag penalty'],
    answer: {
      summary: 'Blue flags tell a slower car that a faster car is approaching to lap it. The slower car must allow the faster car through without unnecessary delay.',
      keyPoints: [
        'Blue flags are most common in races when leaders catch backmarkers.',
        'Ignoring blue flags can lead to penalties.',
        'In practice and qualifying, blue flags can also warn drivers about faster cars on push laps.',
      ],
    },
  },
  {
    id: 'classification-finishing',
    title: 'Race Classification',
    category: 'sporting-rules',
    sourceRefs: ['fiaRegulations'],
    tags: ['classification', 'classified', 'dnf', 'dns', 'dsq', 'lapped'],
    queries: ['what does classified mean', 'what is dnf', 'what is dns', 'what is dsq', 'what does lapped mean'],
    answer: {
      summary: 'Race classification orders drivers by finishing status, laps completed, and elapsed time. A driver can be classified even if they did not take the chequered flag, depending on laps completed and regulations.',
      keyPoints: [
        'DNF means did not finish. DNS means did not start. DSQ means disqualified.',
        'A lapped driver completed fewer laps than the leader but may still be classified.',
        'Official classification should always come from race results data, not local model memory.',
      ],
      routeLiveDataWhen: ['specific race classification', 'driver finishing status', 'official gap'],
    },
  },
  {
    id: 'formation-lap-start',
    title: 'Formation Lap and Race Start',
    category: 'sporting-rules',
    sourceRefs: ['f1Glossary', 'fiaRegulations'],
    tags: ['formation lap', 'start', 'lights out', 'jump start', 'grid'],
    queries: ['what is formation lap', 'how does an f1 race start', 'what is a jump start'],
    answer: {
      summary: 'Before a standing start, cars complete a formation lap to reach the grid, warm tyres and brakes, and prepare systems. The race starts when the start lights go out.',
      keyPoints: [
        'Drivers must keep order and follow start procedure instructions.',
        'A jump start or start-procedure breach can lead to penalties.',
        'If conditions are unsafe, Race Control can use extra formation laps, an aborted start, a Safety Car start, or other procedures.',
      ],
    },
  },
  {
    id: 'wet-weather-racing',
    title: 'Wet Weather Racing',
    category: 'sporting-rules',
    sourceRefs: ['f1Glossary', 'fiaRegulations'],
    tags: ['wet weather', 'intermediate tyres', 'full wets', 'rain', 'standing water'],
    queries: ['how does wet weather work', 'when use intermediate tyres', 'when use full wets', 'why race delayed for rain'],
    answer: {
      summary: 'Wet-weather running depends on grip, visibility, standing water, and tyre choice. Race Control can delay, neutralize, or stop sessions if conditions are unsafe.',
      keyPoints: [
        'Intermediate tyres are for damp or mixed conditions.',
        'Full wet tyres move more water and are used in heavier rain or standing water.',
        'Visibility from spray is often as important as grip when deciding whether racing is safe.',
      ],
      routeLiveDataWhen: ['current weather', 'race delay reason', 'which tyres a driver is using'],
    },
  },
  {
    id: '2026-active-aero',
    title: '2026 Active Aerodynamics',
    category: '2026-regulations',
    sourceRefs: ['f1Regulations2026', 'fiaRegulations'],
    tags: ['2026', 'active aero', 'x-mode', 'z-mode', 'aerodynamics'],
    queries: ['what is active aero', 'what is x mode', 'what is z mode', '2026 aerodynamics'],
    answer: {
      summary: 'The 2026 F1 regulations introduce active aerodynamics concepts so cars can switch aerodynamic states for straights and corners.',
      keyPoints: [
        'The low-drag state is designed for straights and efficiency.',
        'The higher-downforce state is designed for cornering stability and grip.',
        'The aim is to balance efficiency, overtaking, and raceability with the new power-unit rules.',
      ],
      caveats: ['Use current FIA technical regulations for exact activation conditions and terminology.'],
    },
  },
  {
    id: '2026-manual-override',
    title: '2026 Manual Override',
    category: '2026-regulations',
    sourceRefs: ['f1Regulations2026', 'fiaRegulations'],
    tags: ['2026', 'manual override', 'overtaking', 'drs replacement'],
    queries: ['what is manual override', 'what replaces drs in 2026', 'how does 2026 overtaking work'],
    answer: {
      summary: 'Manual Override is part of the 2026 overtaking toolkit. It gives a following driver access to extra electrical deployment under defined conditions.',
      keyPoints: [
        'It is intended to help attacking cars create or complete overtaking opportunities.',
        'It works alongside the broader 2026 aero and power-unit package rather than simply copying old DRS behavior.',
        'Exact use windows, eligibility, and deployment rules should be checked against the current FIA regulations.',
      ],
    },
  },
  {
    id: '2026-power-unit',
    title: '2026 Power Unit Reset',
    category: '2026-regulations',
    sourceRefs: ['f1Regulations2026', 'fiaRegulations'],
    tags: ['2026', 'power unit', 'engine', 'hybrid', 'mgu-h', 'sustainable fuel'],
    queries: ['what changes with 2026 engines', '2026 power unit', 'what happened to mgu-h', '2026 sustainable fuel'],
    answer: {
      summary: 'The 2026 power-unit rules increase the importance of electrical power, remove the MGU-H, and use fully sustainable fuel under the new regulatory framework.',
      keyPoints: [
        'The combustion engine remains a turbocharged V6 concept but works with a much larger electrical contribution than before.',
        'The MGU-H is removed, simplifying the hybrid architecture.',
        'Energy management becomes even more visible because deployment and recovery shape straight-line speed and overtaking.',
      ],
      caveats: ['Exact power figures and homologation details belong to the FIA technical regulations and may be revised.'],
    },
  },
  {
    id: 'cost-cap',
    title: 'Cost Cap',
    category: 'financial-regulations',
    sourceRefs: ['fiaRegulations'],
    tags: ['cost cap', 'budget cap', 'financial regulations'],
    queries: ['what is the cost cap', 'why does f1 have a budget cap', 'what are financial regulations'],
    answer: {
      summary: 'The cost cap limits certain team spending to improve financial sustainability and competitive balance.',
      keyPoints: [
        'Not every expense is included; exclusions and limits are defined in the FIA financial regulations.',
        'The cap affects development strategy because teams must choose where to spend resources.',
        'Breaches can lead to financial and sporting penalties depending on severity.',
      ],
      caveats: ['Use current FIA financial regulations for exact cap values and exclusions.'],
    },
  },
  {
    id: 'super-licence',
    title: 'FIA Super Licence',
    category: 'sporting-rules',
    sourceRefs: ['fiaRegulations'],
    tags: ['super licence', 'driver licence', 'rookie', 'eligibility'],
    queries: ['what is a super licence', 'how do drivers get into f1', 'what licence do f1 drivers need'],
    answer: {
      summary: 'An FIA Super Licence is the authorization a driver needs to race in Formula 1.',
      keyPoints: [
        'Drivers must satisfy FIA eligibility criteria before racing in F1.',
        'The system considers age, experience, licence status, and results in recognized categories.',
        'Penalty points on the licence can create sporting consequences if too many are accumulated.',
      ],
      caveats: ['Exact eligibility thresholds and accepted series are defined by FIA rules and can change.'],
    },
  },
  {
    id: 'glossary-lap-types',
    title: 'Lap Types',
    category: 'glossary',
    sourceRefs: ['f1Glossary'],
    tags: ['out lap', 'in lap', 'flying lap', 'push lap', 'cooldown lap'],
    queries: ['what is an out lap', 'what is an in lap', 'what is a flying lap', 'what is push lap'],
    answer: {
      summary: 'F1 uses different lap terms depending on what the driver is doing before, during, or after a timed effort.',
      keyPoints: [
        'An out lap is the lap after leaving the pits.',
        'A flying lap or push lap is a lap driven at speed to set a time.',
        'An in lap is the lap returning to the pits.',
        'A cooldown lap is used to manage tyres, brakes, battery, or traffic after a push lap.',
      ],
    },
  },
  {
    id: 'glossary-sector-mini-sector',
    title: 'Sectors and Mini-Sectors',
    category: 'glossary',
    sourceRefs: ['f1Glossary'],
    tags: ['sector', 'mini sector', 'purple sector', 'green sector', 'yellow sector'],
    queries: ['what is a sector', 'what is a mini sector', 'what does purple sector mean', 'green sector meaning'],
    answer: {
      summary: 'Each circuit lap is split into sectors, and timing systems may break the lap down further into mini-sectors for more detailed pace analysis.',
      keyPoints: [
        'A purple sector usually means the fastest sector time of the session so far.',
        'A green sector usually means a driver improved their own previous best.',
        'A yellow sector usually means the driver did not improve their personal best in that sector.',
      ],
      routeLiveDataWhen: ['who is fastest in a sector', 'driver sector comparison', 'live mini-sector status'],
    },
  },
  {
    id: 'glossary-apex-racing-line',
    title: 'Apex and Racing Line',
    category: 'glossary',
    sourceRefs: ['f1Glossary'],
    tags: ['apex', 'racing line', 'cornering', 'braking point', 'exit'],
    queries: ['what is an apex', 'what is racing line', 'what is braking point', 'what is corner exit'],
    answer: {
      summary: 'The racing line is the path that best balances speed, distance, grip, and corner exit. The apex is the point near the inside of a corner that the car aims to clip during that path.',
      keyPoints: [
        'A later apex can improve exit speed onto a straight.',
        'A braking point is where the driver starts slowing for a corner.',
        'Corner exit matters heavily because speed carried onto the following straight compounds over distance.',
      ],
      routeLiveDataWhen: ['who braked later', 'who had better apex speed', 'corner telemetry comparison'],
    },
  },
  {
    id: 'car-setup-basics',
    title: 'F1 Car Setup Basics',
    category: 'car-setup',
    sourceRefs: ['f1Glossary', 'fiaRegulations'],
    tags: ['setup', 'ride height', 'wing level', 'balance', 'mechanical setup', 'aero balance'],
    queries: ['what is car setup', 'how do f1 teams set up the car', 'what is setup balance', 'why change wing level'],
    answer: {
      summary: 'F1 car setup is the collection of aerodynamic, suspension, brake, differential, and power-unit choices that tune the car for a circuit and conditions.',
      keyPoints: [
        'Teams trade straight-line speed against cornering grip through wing level and aerodynamic balance.',
        'Mechanical setup affects how the car uses tyres, rides kerbs, brakes, rotates, and puts power down.',
        'Setup is constrained by parc ferme, component limits, safety rules, and the physical operating window of the car.',
      ],
      routeLiveDataWhen: ['specific team setup', 'live setup change', 'driver complaint in a session'],
    },
  },
  {
    id: 'understeer-oversteer',
    title: 'Understeer and Oversteer',
    category: 'car-setup',
    sourceRefs: ['f1Glossary'],
    tags: ['understeer', 'oversteer', 'balance', 'front grip', 'rear grip', 'handling'],
    queries: ['what is understeer', 'what is oversteer', 'why does an f1 car understeer', 'why does an f1 car oversteer'],
    answer: {
      summary: 'Understeer means the front tyres do not turn the car as much as the driver wants. Oversteer means the rear tyres lose grip first and the car rotates too much.',
      keyPoints: [
        'Understeer costs minimum corner speed and can overheat the front tyres.',
        'Oversteer can help rotation but becomes slow or dangerous if the rear is unstable on entry, mid-corner, or exit.',
        'Drivers and engineers tune balance with aero, suspension, differential, brake balance, tyre pressures, and driving style.',
      ],
    },
  },
  {
    id: 'brake-balance',
    title: 'Brake Balance',
    category: 'car-setup',
    sourceRefs: ['f1Glossary'],
    tags: ['brake balance', 'brake bias', 'front brakes', 'rear brakes', 'locking up'],
    queries: ['what is brake balance', 'what is brake bias', 'why move brake bias forward', 'why do f1 cars lock up'],
    answer: {
      summary: 'Brake balance, often called brake bias, controls how braking force is split between the front and rear axles.',
      keyPoints: [
        'More front bias can stabilize braking but may lock the front tyres and create understeer.',
        'More rear bias can help rotation but risks rear locking and instability.',
        'Drivers adjust brake balance during a lap as fuel load, tyre state, temperature, and corner type change.',
      ],
      routeLiveDataWhen: ['specific lock up', 'driver brake balance setting', 'corner telemetry'],
    },
  },
  {
    id: 'differential',
    title: 'Differential Settings',
    category: 'car-setup',
    sourceRefs: ['f1Glossary'],
    tags: ['differential', 'diff', 'entry diff', 'mid corner', 'exit traction', 'wheel speed'],
    queries: ['what is the differential', 'what does diff setting do', 'why change differential in f1'],
    answer: {
      summary: 'The differential controls how the rear wheels rotate relative to each other, shaping corner entry, rotation, and traction on exit.',
      keyPoints: [
        'A more locked differential can improve traction but may make the car resist turning.',
        'A more open differential can help rotation but may reduce drive or stability.',
        'Drivers can adjust differential behavior for entry, mid-corner, and exit phases depending on car balance and tyre condition.',
      ],
    },
  },
  {
    id: 'suspension-platform',
    title: 'Suspension and Aero Platform',
    category: 'car-setup',
    sourceRefs: ['f1Glossary', 'fiaRegulations'],
    tags: ['suspension', 'aero platform', 'ride height', 'kerbs', 'heave', 'roll', 'pitch'],
    queries: ['how does f1 suspension work', 'what is aero platform', 'why ride height matters', 'why are kerbs hard for f1 cars'],
    answer: {
      summary: 'F1 suspension must support tyre grip while keeping the aerodynamic platform in the right window as the car pitches, rolls, brakes, accelerates, and rides kerbs.',
      keyPoints: [
        'A stable platform helps the floor and wings produce consistent downforce.',
        'Too stiff a car can lose grip over bumps and kerbs; too soft a car can lose aerodynamic control or bottom out.',
        'Ground-effect-era cars are especially sensitive to ride height and floor behavior.',
      ],
    },
  },
  {
    id: 'floor-ground-effect',
    title: 'Floor and Ground Effect',
    category: 'car-technology',
    sourceRefs: ['f1Glossary', 'fiaRegulations'],
    tags: ['floor', 'ground effect', 'diffuser', 'venturi', 'downforce', 'porpoising'],
    queries: ['what is ground effect', 'how does the f1 floor work', 'what does diffuser do', 'what is porpoising'],
    answer: {
      summary: 'The floor is a major downforce source. It manages airflow under the car and through the diffuser to create load with less drag than large wing angles.',
      keyPoints: [
        'Ground effect uses shaped underfloor airflow and pressure differences to pull the car toward the track.',
        'The diffuser helps expand and manage airflow exiting the floor.',
        'If the floor stalls or the car runs too close to the track, downforce can fluctuate and create bouncing or porpoising.',
      ],
      routeLiveDataWhen: ['specific upgrade', 'team floor comparison', 'porpoising in a session'],
    },
  },
  {
    id: 'front-wing-role',
    title: 'Front Wing Role',
    category: 'car-technology',
    sourceRefs: ['f1Glossary', 'fiaRegulations'],
    tags: ['front wing', 'aero balance', 'outwash', 'front downforce', 'wing flap'],
    queries: ['what does the front wing do', 'why adjust front wing', 'what is front wing damage'],
    answer: {
      summary: 'The front wing creates front downforce and conditions airflow for the rest of the car.',
      keyPoints: [
        'A front-wing flap change can shift balance toward or away from understeer.',
        'Front-wing damage can hurt both front grip and downstream aero performance.',
        'Front wing design is tightly constrained by the technical regulations.',
      ],
      routeLiveDataWhen: ['specific damage', 'upgrade package', 'team comparison'],
    },
  },
  {
    id: 'rear-wing-beam-wing',
    title: 'Rear Wing and Beam Wing',
    category: 'car-technology',
    sourceRefs: ['f1Glossary', 'fiaRegulations'],
    tags: ['rear wing', 'beam wing', 'drag', 'downforce', 'drs', 'straight line speed'],
    queries: ['what does rear wing do', 'what is beam wing', 'why use low downforce rear wing'],
    answer: {
      summary: 'The rear wing and beam-wing area add rear downforce and drag, shaping stability, straight-line speed, and DRS effectiveness.',
      keyPoints: [
        'More rear wing helps cornering and braking stability but costs speed on straights.',
        'Less rear wing can improve top speed but makes traction and high-speed cornering harder.',
        'The best wing level depends on circuit layout, weather, tyre behavior, and overtaking needs.',
      ],
    },
  },
  {
    id: 'brake-ducts-cooling',
    title: 'Brake Ducts and Cooling',
    category: 'car-technology',
    sourceRefs: ['f1Glossary', 'fiaRegulations'],
    tags: ['brake ducts', 'cooling', 'brake temperature', 'engine cooling', 'bodywork'],
    queries: ['why do f1 cars need cooling', 'what are brake ducts', 'why open cooling louvres', 'why brakes overheat'],
    answer: {
      summary: 'F1 cooling choices manage brakes, power unit, electronics, and hydraulics while minimizing aerodynamic drag and disruption.',
      keyPoints: [
        'More cooling improves reliability and temperature control but usually adds drag or hurts airflow.',
        'Brake ducts cool the brakes and can influence tyre and wheel wake behavior.',
        'Hot races, traffic, altitude, and slow corners can force teams to open bodywork cooling.',
      ],
      routeLiveDataWhen: ['specific overheating issue', 'race retirement reason', 'team cooling package'],
    },
  },
  {
    id: 'tyre-degradation',
    title: 'Tyre Degradation',
    category: 'tyres-strategy',
    sourceRefs: ['f1Glossary'],
    tags: ['tyre degradation', 'deg', 'thermal degradation', 'wear', 'graining', 'blistering'],
    queries: ['what is tyre degradation', 'what is graining', 'what is blistering', 'why do tyres fall off'],
    answer: {
      summary: 'Tyre degradation is the loss of performance over a stint through wear, overheating, surface damage, or compound behavior.',
      keyPoints: [
        'Thermal degradation happens when tyres operate too hot for too long.',
        'Graining occurs when the tyre surface tears and rubber builds up on the tread, reducing grip.',
        'Blistering is internal or surface overheating that creates damaged patches and reduces performance.',
      ],
      routeLiveDataWhen: ['specific driver tyre age', 'stint pace', 'race strategy comparison'],
    },
  },
  {
    id: 'tyre-warmup',
    title: 'Tyre Warm-Up',
    category: 'tyres-strategy',
    sourceRefs: ['f1Glossary'],
    tags: ['tyre warm up', 'out lap', 'blankets', 'temperature window', 'cold tyres'],
    queries: ['why tyre warmup matters', 'what are cold tyres', 'why is out lap important', 'why weave on out lap'],
    answer: {
      summary: 'F1 tyres need to be in a temperature window to deliver grip. Too cold or too hot, and lap time drops quickly.',
      keyPoints: [
        'Drivers warm tyres with braking, steering energy, traction, and careful out-lap preparation.',
        'Cold tyres reduce grip and can cause lockups, snaps, or poor traction.',
        'Qualifying preparation is often about starting the flying lap with each tyre in the right window.',
      ],
    },
  },
  {
    id: 'fuel-load-effect',
    title: 'Fuel Load Effect',
    category: 'race-strategy',
    sourceRefs: ['f1Glossary', 'fiaRegulations'],
    tags: ['fuel load', 'race pace', 'qualifying pace', 'heavy fuel', 'lift and coast'],
    queries: ['why are race laps slower than qualifying', 'how does fuel load affect pace', 'what is lift and coast'],
    answer: {
      summary: 'Race cars start heavier because they carry fuel for the Grand Prix. As fuel burns off, the car usually becomes faster and easier on tyres.',
      keyPoints: [
        'Qualifying laps are lighter, shorter, and run with maximum preparation for one lap.',
        'Race pace balances fuel, tyre life, traffic, battery deployment, and risk.',
        'Lift and coast means lifting off the throttle before braking to save fuel, manage temperatures, or hit energy targets.',
      ],
      routeLiveDataWhen: ['specific team fuel saving', 'race pace comparison', 'stint analysis'],
    },
  },
  {
    id: 'qualifying-track-evolution',
    title: 'Track Evolution',
    category: 'race-strategy',
    sourceRefs: ['f1Glossary'],
    tags: ['track evolution', 'rubbering in', 'track grip', 'qualifying timing', 'traffic'],
    queries: ['what is track evolution', 'why go out late in qualifying', 'why does track get faster', 'what is rubbering in'],
    answer: {
      summary: 'Track evolution is the change in grip as the surface cleans, rubber builds up, temperature changes, and cars run more laps.',
      keyPoints: [
        'A later qualifying run can be faster if the track is improving.',
        'Waiting too long can backfire through yellow flags, red flags, traffic, weather, or not starting the lap in time.',
        'Street circuits often evolve heavily because they begin dirtier and less rubbered-in.',
      ],
    },
  },
  {
    id: 'traffic-management',
    title: 'Traffic Management',
    category: 'race-strategy',
    sourceRefs: ['f1Glossary', 'fiaRegulations'],
    tags: ['traffic', 'dirty air', 'free air', 'gap', 'track position', 'qualifying traffic'],
    queries: ['why traffic matters in f1', 'what is free air', 'why track position matters', 'why qualifying traffic is bad'],
    answer: {
      summary: 'Traffic matters because another car can block clean air, compromise tyre preparation, slow a flying lap, or make a strategy stop ineffective.',
      keyPoints: [
        'Free air means running without a car directly ahead disturbing airflow or pace.',
        'Track position can be worth more than raw pace on circuits where overtaking is hard.',
        'In qualifying, drivers need space to prepare tyres and complete a push lap without being impeded.',
      ],
      routeLiveDataWhen: ['specific impeding incident', 'gap to pit into', 'driver traffic complaint'],
    },
  },
  {
    id: 'pit-window',
    title: 'Pit Window',
    category: 'race-strategy',
    sourceRefs: ['f1Glossary'],
    tags: ['pit window', 'pit lane loss', 'strategy window', 'undercut', 'overcut'],
    queries: ['what is pit window', 'what is pit lane loss', 'when should a driver pit', 'what does pit window mean'],
    answer: {
      summary: 'A pit window is the range of laps where a stop can work strategically, considering tyre life, pit lane loss, traffic, track position, and race interruptions.',
      keyPoints: [
        'Pit lane loss is the time lost entering, stopping, and exiting the pits compared with staying on track.',
        'A driver needs enough gap to rejoin in clear air or ahead of a rival depending on the plan.',
        'Safety Cars and VSCs can make pitting cheaper because the field is slowed.',
      ],
      routeLiveDataWhen: ['specific race pit window', 'gap needed to pit', 'driver tyre age'],
    },
  },
  {
    id: 'safety-car-strategy',
    title: 'Safety Car Strategy Effect',
    category: 'race-strategy',
    sourceRefs: ['f1Glossary', 'fiaRegulations'],
    tags: ['safety car strategy', 'cheap pit stop', 'vsc strategy', 'neutralization', 'restart'],
    queries: ['why pit under safety car', 'why safety car changes strategy', 'what is cheap pit stop', 'why vsc helps pit stops'],
    answer: {
      summary: 'Safety Car and VSC periods can change strategy because the cars on track are slowed, reducing the relative time lost by pitting.',
      keyPoints: [
        'A cheap stop can let a driver change tyres while losing fewer positions than under green-flag racing.',
        'The benefit depends on where the driver is, gaps, pit entry timing, and whether the pit lane is open.',
        'Restarts also reset tyre temperature, track position, and overtaking opportunities.',
      ],
      routeLiveDataWhen: ['specific safety car beneficiary', 'lap of safety car', 'pit stop timing in a race'],
    },
  },
  {
    id: 'engine-component-penalties',
    title: 'Power Unit Component Penalties',
    category: 'sporting-rules',
    sourceRefs: ['fiaRegulations'],
    tags: ['engine penalty', 'power unit components', 'grid penalty', 'component limit', 'gearbox penalty'],
    queries: ['why engine penalty', 'what is power unit penalty', 'why grid penalty for engine', 'what are component limits'],
    answer: {
      summary: 'F1 limits certain power-unit and gearbox components across a season. Exceeding those allowances can trigger grid penalties under FIA rules.',
      keyPoints: [
        'Limits encourage reliability and cost control rather than unlimited component replacement.',
        'Teams may accept penalties strategically at circuits where overtaking is easier or when a component is near failure.',
        'Exact allowances and penalty scales are regulation-specific and can change by season.',
      ],
      routeLiveDataWhen: ['specific driver penalty', 'current component pool', 'grid drop at an event'],
    },
  },
  {
    id: 'impeding-rules',
    title: 'Impeding',
    category: 'sporting-rules',
    sourceRefs: ['fiaRegulations'],
    tags: ['impeding', 'blocking', 'qualifying traffic', 'stewards', 'penalty'],
    queries: ['what is impeding', 'why impeding penalty', 'what counts as blocking in qualifying'],
    answer: {
      summary: 'Impeding is when a driver unnecessarily obstructs another driver, especially during practice or qualifying when one car is on a push lap and another is not.',
      keyPoints: [
        'Stewards consider positioning, speed difference, radio warnings, visibility, and whether the affected driver lost a meaningful opportunity.',
        'Qualifying impeding can lead to grid penalties or reprimands depending on severity and rules.',
        'Drivers still have responsibility to manage track position safely even when preparing their own tyres.',
      ],
      routeLiveDataWhen: ['specific impeding incident', 'stewards decision', 'qualifying investigation'],
    },
  },
  {
    id: 'unsafe-release',
    title: 'Unsafe Release',
    category: 'sporting-rules',
    sourceRefs: ['fiaRegulations'],
    tags: ['unsafe release', 'pit lane', 'pit stop penalty', 'release into traffic'],
    queries: ['what is unsafe release', 'why unsafe release penalty', 'what happens if team releases car unsafely'],
    answer: {
      summary: 'An unsafe release happens when a team sends a car out of its pit box in a way that creates danger or interferes with another car in the pit lane.',
      keyPoints: [
        'Unsafe releases can involve contact, forcing another car to brake or swerve, loose wheels, or releasing into the path of another car.',
        'Penalties can apply to the driver or team depending on the incident and regulations.',
        'Pit lane safety is tightly controlled because crews and cars operate in confined space.',
      ],
      routeLiveDataWhen: ['specific pit incident', 'stewards decision', 'pit stop release replay'],
    },
  },
  {
    id: 'red-flag-tyres-repairs',
    title: 'Red Flag Tyres and Repairs',
    category: 'sporting-rules',
    sourceRefs: ['fiaRegulations'],
    tags: ['red flag tyres', 'red flag repairs', 'suspended race', 'restart procedure'],
    queries: ['can teams change tyres under red flag', 'can teams repair cars under red flag', 'why red flag helps strategy'],
    answer: {
      summary: 'During a red flag, the session is suspended and teams may be allowed to perform certain work under the sporting regulations and race-control instructions.',
      keyPoints: [
        'Tyre changes and repairs during a suspension can alter strategy because cars are not losing normal pit-stop time.',
        'Allowed work depends on current rules, event instructions, and safety procedures.',
        'Restart order, lap count, and tyre requirements can create edge cases that need official race data for a specific event.',
      ],
      routeLiveDataWhen: ['specific red flag race', 'restart order', 'who changed tyres under red flag'],
    },
  },
  {
    id: 'scrutineering',
    title: 'Scrutineering',
    category: 'sporting-rules',
    sourceRefs: ['fiaRegulations'],
    tags: ['scrutineering', 'technical inspection', 'plank', 'weight', 'legality', 'disqualification'],
    queries: ['what is scrutineering', 'how does f1 check legality', 'why car disqualified after race', 'what is plank wear'],
    answer: {
      summary: 'Scrutineering is the FIA technical inspection process that checks cars against the regulations before, during, and after events.',
      keyPoints: [
        'Checks can include weight, dimensions, bodywork, plank wear, fuel samples, software, and component legality.',
        'A car can be disqualified if it fails post-session technical checks, even after finishing on track.',
        'Teams must operate within both the written rules and FIA measurement procedures.',
      ],
      routeLiveDataWhen: ['specific disqualification', 'post-race technical report', 'plank wear measurement'],
    },
  },
  {
    id: 'driver-license-penalty-points',
    title: 'Licence Penalty Points',
    category: 'sporting-rules',
    sourceRefs: ['fiaRegulations'],
    tags: ['penalty points', 'licence points', 'race ban', 'super licence'],
    queries: ['what are penalty points', 'how do f1 penalty points work', 'can penalty points cause race ban'],
    answer: {
      summary: 'Licence penalty points are added to a driver’s FIA licence for certain offences and can lead to a race ban if enough accumulate within the defined period.',
      keyPoints: [
        'Penalty points are separate from championship points and time penalties.',
        'They are intended to discourage repeated unsafe or unsporting behavior.',
        'Exact totals and expiry dates should come from current FIA/stewards data for a specific driver.',
      ],
      routeLiveDataWhen: ['specific driver penalty point total', 'expiry date', 'recent stewarding decision'],
    },
  },
  {
    id: 'team-roles',
    title: 'Key F1 Team Roles',
    category: 'f1-basics',
    sourceRefs: ['f1WhatIsF1', 'f1Glossary'],
    tags: ['team principal', 'race engineer', 'strategist', 'mechanics', 'pit wall', 'sporting director'],
    queries: ['what does race engineer do', 'what does team principal do', 'who is on the pit wall', 'what does strategist do'],
    answer: {
      summary: 'An F1 team combines leadership, engineering, strategy, operations, mechanics, and support staff to run two cars through a race weekend.',
      keyPoints: [
        'The team principal leads the organization and represents it publicly and politically.',
        'Race engineers are the main link between driver and car crew during sessions.',
        'Strategists model tyre, traffic, Safety Car, and competitor scenarios to recommend race decisions.',
      ],
    },
  },
  {
    id: 'radio-messages',
    title: 'Team Radio',
    category: 'f1-basics',
    sourceRefs: ['f1Glossary', 'fiaRegulations'],
    tags: ['team radio', 'race engineer', 'driver radio', 'pit wall', 'messages'],
    queries: ['how does team radio work', 'what can teams tell drivers', 'why radio messages matter'],
    answer: {
      summary: 'Team radio is the communication link between driver and team, used for safety information, strategy, car management, traffic, and driver feedback.',
      keyPoints: [
        'Engineers use short coded language because the driver is busy and rivals may hear broadcasts later.',
        'Radio can cover tyre management, brake temperatures, battery state, gaps, flags, and strategy instructions.',
        'Some radio content is broadcast publicly, but teams also use internal data channels and pit-wall tools.',
      ],
      routeLiveDataWhen: ['specific radio message', 'team radio transcript', 'driver complaint in a session'],
    },
  },
  {
    id: 'development-upgrades',
    title: 'Car Development and Upgrades',
    category: 'car-technology',
    sourceRefs: ['f1Glossary', 'fiaRegulations'],
    tags: ['upgrade', 'development', 'aero package', 'wind tunnel', 'cfd', 'correlation'],
    queries: ['how do f1 upgrades work', 'what is correlation', 'why upgrades fail', 'what is aero package'],
    answer: {
      summary: 'F1 teams develop upgrades through simulation, CFD, wind-tunnel work, manufacturing, and track validation, but performance depends on correlation with the real car.',
      keyPoints: [
        'Correlation means the factory tools predict what the car does on track accurately enough to guide development.',
        'An upgrade can add downforce but still fail if it narrows the setup window, hurts balance, or behaves poorly in yaw, ride height, or traffic.',
        'Cost cap and aerodynamic testing restrictions force teams to prioritize development paths carefully.',
      ],
      routeLiveDataWhen: ['specific team upgrade', 'race weekend package', 'upgrade comparison'],
    },
  },
  {
    id: 'aero-testing-restrictions',
    title: 'Aerodynamic Testing Restrictions',
    category: 'financial-regulations',
    sourceRefs: ['fiaRegulations'],
    tags: ['atr', 'wind tunnel', 'cfd', 'aero testing', 'sliding scale'],
    queries: ['what is atr', 'how do aero testing restrictions work', 'why slower teams get more wind tunnel time'],
    answer: {
      summary: 'Aerodynamic Testing Restrictions limit wind-tunnel and CFD work, with a sliding scale that gives lower-ranked teams more development allowance than higher-ranked teams.',
      keyPoints: [
        'The system is designed to support competitive balance without equalizing budgets entirely.',
        'Teams must choose carefully how to spend limited aero testing runs.',
        'Exact allowances depend on FIA rules and championship position at defined assessment points.',
      ],
      routeLiveDataWhen: ['current ATR allowance', 'team ranking at assessment point'],
    },
  },
  {
    id: 'driver-market-contracts',
    title: 'Driver Market Basics',
    category: 'f1-basics',
    sourceRefs: ['f1WhatIsF1'],
    tags: ['driver market', 'contracts', 'silly season', 'academy', 'reserve driver'],
    queries: ['what is silly season', 'how do f1 driver contracts work', 'what is reserve driver', 'what is driver academy'],
    answer: {
      summary: 'The F1 driver market is the movement of drivers between seats, influenced by contracts, performance, sponsorship, team strategy, junior programs, and regulation changes.',
      keyPoints: [
        'Silly season is the informal term for intense speculation and negotiation around future driver lineups.',
        'Reserve and test drivers support simulator work, development, and substitute driving if needed.',
        'Driver academies help teams develop young talent for future F1 opportunities.',
      ],
      routeLiveDataWhen: ['current contract status', 'rumour', 'official lineup announcement'],
    },
  },
  {
    id: 'rookie-testing',
    title: 'Rookie Running and Testing',
    category: 'sporting-rules',
    sourceRefs: ['fiaRegulations'],
    tags: ['rookie', 'fp1 rookie', 'testing', 'young driver', 'reserve driver'],
    queries: ['why rookies drive in fp1', 'what is rookie fp1', 'how do young drivers test f1 cars'],
    answer: {
      summary: 'F1 rules include opportunities or requirements for teams to run eligible rookie drivers in certain practice or test contexts.',
      keyPoints: [
        'Rookie running helps young drivers gain F1 experience and gives teams evaluation data.',
        'Teams balance rookie sessions against race-driver preparation and car-development work.',
        'Eligibility and session requirements are defined by current sporting regulations.',
      ],
      routeLiveDataWhen: ['specific rookie session', 'driver eligibility', 'team FP1 plan'],
    },
  },
  {
    id: 'safety-halo',
    title: 'Halo and Cockpit Safety',
    category: 'safety',
    sourceRefs: ['f1WhatIsF1', 'fiaRegulations'],
    tags: ['halo', 'cockpit safety', 'survival cell', 'head protection', 'crash structure'],
    queries: ['what is halo', 'why does f1 have halo', 'how are f1 drivers protected', 'what is survival cell'],
    answer: {
      summary: 'The halo is a cockpit protection structure designed to deflect large objects and protect the driver’s head in severe incidents.',
      keyPoints: [
        'The driver sits inside a survival cell with crash structures, harnesses, headrests, fire-resistant systems, and strict FIA safety standards.',
        'The halo was controversial visually at introduction but has since been credited in multiple serious accidents.',
        'Safety devices are regulated and tested through FIA crash and load requirements.',
      ],
    },
  },
  {
    id: 'hans-device',
    title: 'HANS Device',
    category: 'safety',
    sourceRefs: ['f1Glossary', 'fiaRegulations'],
    tags: ['hans', 'head and neck support', 'safety', 'helmet', 'harness'],
    queries: ['what is hans device', 'why drivers wear hans', 'what protects driver neck'],
    answer: {
      summary: 'The HANS device, short for Head and Neck Support, helps control head movement during a crash to reduce neck and skull-base injury risk.',
      keyPoints: [
        'It works with the helmet and harness to limit dangerous forward head motion.',
        'It is part of the wider driver safety system alongside the survival cell, halo, seat, belts, and fire protection.',
        'Its use is mandated in modern top-level FIA competition.',
      ],
    },
  },
  {
    id: 'medical-car-marshals',
    title: 'Medical Car and Marshals',
    category: 'safety',
    sourceRefs: ['fiaRegulations'],
    tags: ['medical car', 'marshals', 'rescue', 'incident response', 'trackside safety'],
    queries: ['what does medical car do', 'what do marshals do', 'how f1 handles crashes', 'who responds to accidents'],
    answer: {
      summary: 'F1 events rely on marshals, medical crews, rescue teams, Race Control, and trackside procedures to respond quickly to incidents.',
      keyPoints: [
        'Marshals handle flags, recovery, local safety, and trackside response under race-control coordination.',
        'The medical car and extraction teams are positioned to reach serious incidents quickly.',
        'Safety Car, VSC, red flags, and local yellow flags are tools to protect drivers, officials, and crews during incidents.',
      ],
      routeLiveDataWhen: ['specific crash response', 'race delay reason', 'red flag incident'],
    },
  },
  {
    id: 'f1-eras-overview',
    title: 'F1 Rules Eras Overview',
    category: 'history',
    sourceRefs: ['f1WhatIsF1', 'fiaRegulations'],
    tags: ['f1 history', 'rules eras', 'turbo hybrid', 'ground effect', 'v10', 'v8', 'regulation era'],
    queries: ['what are f1 eras', 'how has f1 changed', 'what is turbo hybrid era', 'what was v10 era'],
    answer: {
      summary: 'F1 history is often discussed through regulation eras because engine formula, aerodynamics, tyres, safety, and sporting rules shape how cars race.',
      keyPoints: [
        'Engine eras include naturally aspirated periods, turbo periods, V10/V8 eras, and the modern turbo-hybrid era.',
        'Aerodynamic rules have shifted between high-outwash concepts, simplified wings, and modern ground-effect floors.',
        'Safety, cost control, sustainability, and overtaking have become increasingly central to rule design.',
      ],
      caveats: ['For specific season results or car specifications, use historical data sources rather than model memory alone.'],
    },
  },
  {
    id: 'points-history',
    title: 'Points System History Context',
    category: 'history',
    sourceRefs: ['fiaRegulations'],
    tags: ['points history', 'old points system', 'scoring history', 'championship format'],
    queries: ['did f1 always use same points', 'old f1 points system', 'how did f1 points change'],
    answer: {
      summary: 'F1 has changed its points system many times, including how many positions score, values for wins and podiums, and special rules for shortened races or fastest laps.',
      keyPoints: [
        'Modern points should not be applied backward to older seasons without checking that year’s rules.',
        'Historical championships can be affected by dropped scores, different scoring depth, and event-specific rules.',
        'Current-season questions should use current FIA sporting regulations and official classification data.',
      ],
      routeLiveDataWhen: ['specific season points table', 'historical championship recalculation', 'race result'],
    },
  },
  {
    id: 'grand-slam-hat-trick',
    title: 'Grand Slam and Hat-Trick',
    category: 'glossary',
    sourceRefs: ['f1Glossary'],
    tags: ['grand slam', 'hat trick', 'pole', 'fastest lap', 'lead every lap'],
    queries: ['what is f1 grand slam', 'what is f1 hat trick', 'what is grand chelem', 'what does grand slam mean in f1'],
    answer: {
      summary: 'An F1 hat-trick usually means pole position, race win, and fastest lap in the same Grand Prix. A Grand Slam, or Grand Chelem, adds leading every lap.',
      keyPoints: [
        'These are statistical achievements, not separate championship awards.',
        'Definitions can vary slightly in casual use, so specify the criteria when discussing records.',
        'For a specific driver count, use historical results data.',
      ],
      routeLiveDataWhen: ['specific record count', 'whether a driver achieved one in a race'],
    },
  },
  {
    id: 'pole-position',
    title: 'Pole Position',
    category: 'glossary',
    sourceRefs: ['f1Glossary', 'fiaRegulations'],
    tags: ['pole position', 'qualifying', 'grid', 'fastest qualifier'],
    queries: ['what is pole position', 'who gets pole', 'why pole matters', 'does penalty affect pole'],
    answer: {
      summary: 'Pole position is the first starting position on the grid, normally earned by the fastest driver in the final qualifying segment before grid penalties are applied under the relevant rules.',
      keyPoints: [
        'Pole matters because clean air and track position are powerful advantages at many circuits.',
        'Grid penalties can change the actual starting order even when a driver set the fastest qualifying time.',
        'Sprint weekends and historical formats can complicate how pole is recorded.',
      ],
      routeLiveDataWhen: ['specific race pole sitter', 'current grid penalties', 'qualifying result'],
    },
  },
  {
    id: 'outwash-inwash',
    title: 'Outwash and Inwash',
    category: 'car-technology',
    sourceRefs: ['f1Glossary', 'fiaRegulations'],
    tags: ['outwash', 'inwash', 'wheel wake', 'aerodynamics', 'dirty air'],
    queries: ['what is outwash', 'what is inwash', 'why wheel wake matters', 'what is wake control'],
    answer: {
      summary: 'Outwash and inwash describe how aerodynamic surfaces direct airflow around or inward from parts of the car, especially around the front wheels and floor.',
      keyPoints: [
        'Wheel wake is turbulent and can hurt downstream aerodynamic performance.',
        'Regulations often target wake-control tricks to improve racing and reduce dirty air.',
        'Teams still try to manage airflow legally within the geometry allowed by the technical rules.',
      ],
    },
  },
  {
    id: 'drag-downforce-efficiency',
    title: 'Drag, Downforce, and Efficiency',
    category: 'car-technology',
    sourceRefs: ['f1Glossary'],
    tags: ['drag', 'downforce', 'aero efficiency', 'lift drag ratio', 'straight line speed', 'corner speed'],
    queries: ['what is aero efficiency', 'difference between drag and downforce', 'why more downforce is not always better'],
    answer: {
      summary: 'Downforce pushes the car into the track for grip, while drag resists forward motion. Aero efficiency is about producing useful downforce for the least drag cost.',
      keyPoints: [
        'More downforce helps braking and cornering but can reduce top speed.',
        'Low-drag setups help on straights but may hurt tyre life or corner speed.',
        'Teams choose aero levels based on circuit layout, weather, traffic, and race strategy.',
      ],
    },
  },
  {
    id: 'battery-deployment',
    title: 'Battery Deployment and Harvesting',
    category: 'car-technology',
    sourceRefs: ['f1Glossary', 'fiaRegulations'],
    tags: ['battery deployment', 'harvesting', 'ers', 'state of charge', 'soc', 'energy management'],
    queries: ['what is battery deployment', 'what is harvesting', 'what is state of charge', 'why drivers recharge battery'],
    answer: {
      summary: 'Hybrid F1 cars recover electrical energy and deploy it for performance. Drivers and control systems manage state of charge across a lap and race.',
      keyPoints: [
        'Harvesting recovers energy, often through braking via the MGU-K.',
        'Deployment uses stored energy to add power and shape acceleration or overtaking.',
        'Energy management can force trade-offs between attack, defence, lap time, and future-lap recovery.',
      ],
      routeLiveDataWhen: ['specific battery state', 'driver deployment map', 'telemetry comparison'],
    },
  },
  {
    id: 'race-start-procedure',
    title: 'Race Start Procedure',
    category: 'sporting-rules',
    sourceRefs: ['fiaRegulations', 'f1Glossary'],
    tags: ['race start', 'lights out', 'formation lap', 'aborted start', 'jump start', 'start procedure'],
    queries: ['how does lights out work', 'what is aborted start', 'what is jump start', 'why extra formation lap'],
    answer: {
      summary: 'A normal F1 race start follows a formation lap, grid positioning, start-light sequence, and launch when the lights go out.',
      keyPoints: [
        'If a car has a problem or conditions are unsafe, Race Control can abort the start or require another formation lap.',
        'A jump start or incorrect grid position can lead to penalties.',
        'Wet or unsafe conditions may produce a Safety Car start instead of a normal standing start.',
      ],
      routeLiveDataWhen: ['specific aborted start', 'jump start penalty', 'race start incident'],
    },
  },
  {
    id: 'pit-stop-penalty-serving',
    title: 'Serving Time Penalties',
    category: 'sporting-rules',
    sourceRefs: ['fiaRegulations'],
    tags: ['time penalty', 'serving penalty', 'pit stop', 'five second penalty', 'ten second penalty'],
    queries: ['how serve time penalty', 'can teams work on car during penalty', 'what is five second penalty served at pit stop'],
    answer: {
      summary: 'Some time penalties can be served during a pit stop or added to race time, depending on the penalty type, timing, and sporting regulations.',
      keyPoints: [
        'When serving a stop-time penalty, the car must normally remain stationary for the penalty duration before work begins.',
        'If the driver does not pit again, the penalty may be added to elapsed race time after the finish.',
        'Penalty rules have edge cases, especially around Safety Cars, red flags, retirement, and post-race decisions.',
      ],
      routeLiveDataWhen: ['specific penalty service', 'post-race classification', 'stewards document'],
    },
  },
  {
    id: 'blue-flag-strategy',
    title: 'Blue Flags and Race Strategy',
    category: 'race-strategy',
    sourceRefs: ['fiaRegulations', 'f1Glossary'],
    tags: ['blue flags', 'lapping traffic', 'backmarkers', 'race strategy', 'leaders'],
    queries: ['how blue flags affect strategy', 'why backmarkers matter', 'why leaders lose time lapping cars'],
    answer: {
      summary: 'Blue flags can affect strategy because leaders must pass lapped traffic while managing tyres, gaps, and risk.',
      keyPoints: [
        'Even when backmarkers comply, the leader may lose rhythm, tyre temperature, or time in dirty air.',
        'Traffic can change undercut and overcut calculations by delaying one driver more than another.',
        'Drivers being lapped must allow leaders through without unnecessary delay under the rules.',
      ],
      routeLiveDataWhen: ['specific lapping sequence', 'driver time lost behind traffic'],
    },
  },
  {
    id: 'constructor-vs-customer-teams',
    title: 'Works and Customer Teams',
    category: 'f1-basics',
    sourceRefs: ['f1WhatIsF1', 'fiaRegulations'],
    tags: ['works team', 'customer team', 'engine supplier', 'power unit supplier', 'constructor'],
    queries: ['what is works team', 'what is customer team', 'difference between constructor and engine supplier', 'why teams buy engines'],
    answer: {
      summary: 'An F1 constructor designs and enters its own chassis, but it may build its own power unit or buy one from another manufacturer.',
      keyPoints: [
        'A works team has close factory alignment with a power-unit manufacturer or is the manufacturer’s own team.',
        'A customer team buys power units and sometimes other allowed components from a supplier.',
        'The regulations define which parts teams must design themselves and which parts can be supplied.',
      ],
      routeLiveDataWhen: ['current engine supplier list', 'future team partnership'],
    },
  },
  {
    id: 'listed-team-components',
    title: 'Listed Team Components',
    category: 'technical-regulations',
    sourceRefs: ['fiaRegulations'],
    tags: ['listed team components', 'ltc', 'transferable components', 'customer parts', 'constructor rules'],
    queries: ['what are listed team components', 'what parts must teams design', 'what are transferable components', 'can f1 teams share parts'],
    answer: {
      summary: 'F1 rules separate components teams must design themselves from components that may be supplied or transferred under defined conditions.',
      keyPoints: [
        'Listed Team Components are central to constructor identity and must be controlled by the team under the rules.',
        'Other parts may be transferable, supplied, or standardized depending on the regulations.',
        'These rules aim to balance independent competition, cost control, and practical supplier relationships.',
      ],
      caveats: ['Exact component categories are technical-regulation details and can change by season.'],
    },
  },
  {
    id: 'driver-aids',
    title: 'Driver Aids',
    category: 'technical-regulations',
    sourceRefs: ['fiaRegulations'],
    tags: ['driver aids', 'traction control', 'abs', 'launch control', 'manual driving'],
    queries: ['does f1 have traction control', 'does f1 have abs', 'are driver aids allowed', 'why no traction control'],
    answer: {
      summary: 'Modern F1 restricts driver aids so drivers must manage traction, braking, starts, and car control within the permitted electronic systems.',
      keyPoints: [
        'Traction control and ABS are not used as normal driver aids in modern F1.',
        'Teams still use complex legal control systems for power-unit, differential, brake migration, and energy management.',
        'The rules aim to preserve driver skill while allowing highly technical car systems.',
      ],
      caveats: ['Driver-aid legality is regulation-specific and has changed historically.'],
    },
  },
  {
    id: 'minimum-weight-ballast',
    title: 'Minimum Weight and Ballast',
    category: 'technical-regulations',
    sourceRefs: ['fiaRegulations'],
    tags: ['minimum weight', 'ballast', 'car weight', 'driver weight', 'technical regulations'],
    queries: ['why f1 cars have minimum weight', 'what is ballast', 'how car weight affects lap time', 'why drivers get weighed'],
    answer: {
      summary: 'F1 cars must meet minimum weight rules. Ballast can be used to reach the limit and tune weight distribution within legal constraints.',
      keyPoints: [
        'Lower weight generally helps acceleration, braking, tyre load, and direction changes.',
        'Minimum weight rules prevent unsafe or excessively costly weight reduction and help equalize competition.',
        'Driver and car weight procedures are part of FIA technical and sporting checks.',
      ],
      routeLiveDataWhen: ['specific car weight breach', 'post-qualifying weighbridge incident'],
    },
  },
  {
    id: 'fuel-samples',
    title: 'Fuel Samples',
    category: 'technical-regulations',
    sourceRefs: ['fiaRegulations'],
    tags: ['fuel sample', 'fuel legality', 'fuel quantity', 'technical inspection'],
    queries: ['why fuel sample required', 'what happens if not enough fuel', 'why car stopped after qualifying', 'fuel sample rules'],
    answer: {
      summary: 'FIA fuel rules require cars to use legal fuel and often require enough fuel to provide a sample after sessions.',
      keyPoints: [
        'A car may be investigated or penalized if it cannot provide the required sample or if the fuel does not match legal requirements.',
        'Teams manage fuel load tightly because carrying extra fuel costs lap time.',
        'Fuel procedures and sample requirements are technical-regulation details.',
      ],
      routeLiveDataWhen: ['specific fuel sample penalty', 'qualifying disqualification', 'stewards document'],
    },
  },
  {
    id: 'closed-open-pit-lane',
    title: 'Open and Closed Pit Lane',
    category: 'sporting-rules',
    sourceRefs: ['fiaRegulations'],
    tags: ['pit lane closed', 'pit lane open', 'red flag', 'safety car', 'race control'],
    queries: ['what means pit lane closed', 'why pit lane closed', 'when pit lane opens', 'can drivers pit under red flag'],
    answer: {
      summary: 'Race Control can control when the pit lane is open or closed for safety, session procedures, starts, red flags, or incident management.',
      keyPoints: [
        'Entering a closed pit lane can trigger penalties unless specific exceptions apply.',
        'Pit lane status can affect strategy dramatically during Safety Car, VSC, red flag, or start procedures.',
        'The exact rule depends on session state and official race-control messages.',
      ],
      routeLiveDataWhen: ['current pit lane status', 'specific race-control message', 'driver penalty for closed pit lane'],
    },
  },
  {
    id: 'session-types',
    title: 'Session Types',
    category: 'weekend-format',
    sourceRefs: ['f1WhatIsF1', 'fiaRegulations'],
    tags: ['practice', 'qualifying', 'sprint', 'race', 'session', 'free practice'],
    queries: ['what are f1 session types', 'difference between practice qualifying race', 'what is free practice', 'what is sprint qualifying'],
    answer: {
      summary: 'A race weekend is split into sessions with different purposes: practice for preparation, qualifying for grid order, Sprint sessions on selected weekends, and the Grand Prix for the main points result.',
      keyPoints: [
        'Practice focuses on setup, tyre learning, race runs, qualifying simulation, and systems checks.',
        'Qualifying prioritizes peak lap time and track position.',
        'The race rewards execution over a long distance: starts, tyre life, pit stops, traffic, reliability, and strategy.',
      ],
      caveats: ['Exact session order depends on standard or Sprint weekend format and current regulations.'],
    },
  },
  {
    id: 'driver-preparation',
    title: 'Driver Preparation',
    category: 'f1-basics',
    sourceRefs: ['f1WhatIsF1'],
    tags: ['driver preparation', 'simulator', 'track walk', 'fitness', 'reaction', 'heat training'],
    queries: ['how do f1 drivers prepare', 'why drivers use simulator', 'what is track walk', 'how fit are f1 drivers'],
    answer: {
      summary: 'F1 drivers prepare through simulator work, engineering briefings, physical training, heat acclimation, reaction work, media duties, and track familiarization.',
      keyPoints: [
        'Simulator work helps drivers learn setup directions, procedures, and circuit details before running on track.',
        'Physical preparation targets neck strength, heat tolerance, endurance, reaction, and mental load.',
        'Track walks and briefings help drivers understand kerbs, bumps, grip changes, and risk areas.',
      ],
    },
  },
  {
    id: 'simulator-role',
    title: 'Simulator Role',
    category: 'car-technology',
    sourceRefs: ['f1WhatIsF1'],
    tags: ['simulator', 'driver in loop', 'setup work', 'development', 'correlation'],
    queries: ['how f1 simulator works', 'what is driver in loop simulator', 'why teams use simulator', 'simulator correlation'],
    answer: {
      summary: 'F1 simulators let teams and drivers test setup directions, procedures, and development ideas before or during race weekends.',
      keyPoints: [
        'Driver-in-loop simulation combines a driver with a virtual car and circuit model.',
        'Simulator usefulness depends on correlation with the real car and tyre behavior.',
        'Reserve and simulator drivers can support race drivers by testing setup changes while a weekend is happening.',
      ],
      routeLiveDataWhen: ['specific team simulator programme', 'current setup decision'],
    },
  },
  {
    id: 'racecraft-defending',
    title: 'Racecraft and Defending',
    category: 'driving-technique',
    sourceRefs: ['f1Glossary', 'fiaRegulations'],
    tags: ['racecraft', 'defending', 'overtaking', 'racing line', 'leaving space', 'forcing off track'],
    queries: ['what is racecraft', 'how do drivers defend', 'what is leaving space', 'what counts as forcing off track'],
    answer: {
      summary: 'Racecraft is the skill of attacking, defending, positioning, judging grip, and racing within the rules while minimizing time loss and risk.',
      keyPoints: [
        'Defending involves choosing lines, managing exits, and placing the car to reduce an opponent’s options.',
        'Drivers must still respect racing rules around crowding, contact, and track limits.',
        'Stewards judge incidents from context: overlap, corner phase, car control, track limits, and whether space was required.',
      ],
      routeLiveDataWhen: ['specific incident', 'stewards judgement', 'onboard comparison'],
    },
  },
  {
    id: 'overtaking-techniques',
    title: 'Overtaking Techniques',
    category: 'driving-technique',
    sourceRefs: ['f1Glossary'],
    tags: ['overtaking', 'divebomb', 'switchback', 'dummy', 'late braking', 'traction'],
    queries: ['how do f1 drivers overtake', 'what is switchback', 'what is divebomb', 'what is dummy move'],
    answer: {
      summary: 'F1 overtaking combines straight-line speed, braking confidence, tyre grip, battery deployment, positioning, and forcing a rival into compromise.',
      keyPoints: [
        'A switchback means sacrificing corner entry to get a better exit when the rival runs deep or defends the inside.',
        'A late-braking move can work if the attacker remains in control and respects racing room rules.',
        'A dummy move pressures the defender into covering one line before the attacker switches to another.',
      ],
      routeLiveDataWhen: ['specific overtake', 'braking telemetry', 'onboard analysis'],
    },
  },
  {
    id: 'qualifying-prep-lap',
    title: 'Qualifying Prep Lap',
    category: 'driving-technique',
    sourceRefs: ['f1Glossary'],
    tags: ['prep lap', 'out lap', 'tyre preparation', 'qualifying', 'track position'],
    queries: ['what is prep lap', 'why qualifying out lap slow', 'how drivers prepare tyres for qualifying', 'why drivers back up before flying lap'],
    answer: {
      summary: 'A qualifying prep lap is used to place the tyres, brakes, battery, and track position in the right window before the flying lap.',
      keyPoints: [
        'Drivers may drive slowly to cool or prepare tyres, then use braking and acceleration to target temperatures.',
        'Backing up can create space to the car ahead but risks traffic behind.',
        'A poor prep lap can ruin the flying lap before it starts.',
      ],
      routeLiveDataWhen: ['specific qualifying lap', 'traffic investigation', 'driver out-lap trace'],
    },
  },
  {
    id: 'engine-modes',
    title: 'Engine Modes and Power Unit Maps',
    category: 'car-technology',
    sourceRefs: ['f1Glossary', 'fiaRegulations'],
    tags: ['engine mode', 'power unit map', 'strat mode', 'party mode', 'energy mode'],
    queries: ['what are engine modes', 'what is strat mode', 'what was party mode', 'why change engine mode'],
    answer: {
      summary: 'Power-unit modes or maps control how the engine and hybrid systems deliver, recover, and manage energy within the rules.',
      keyPoints: [
        'Modes can affect power delivery, harvesting, reliability, fuel use, temperatures, and battery state.',
        'Rules have restricted some qualifying-specific engine-mode practices over time.',
        'Teams still manage many legal settings for drivability, reliability, and energy targets.',
      ],
      caveats: ['Exact mode names are team-specific and often not public.'],
    },
  },
  {
    id: 'gearbox-transmission',
    title: 'Gearbox and Transmission',
    category: 'car-technology',
    sourceRefs: ['f1Glossary', 'fiaRegulations'],
    tags: ['gearbox', 'transmission', 'gears', 'shift', 'drivetrain', 'gear ratios'],
    queries: ['how f1 gearbox works', 'what is gearbox penalty', 'why gear ratios matter', 'how fast f1 cars shift'],
    answer: {
      summary: 'The gearbox transfers power to the rear wheels and must survive high loads, rapid shifts, kerb strikes, and long race distances under component rules.',
      keyPoints: [
        'Gear selection affects acceleration, top speed, traction, and power-unit efficiency.',
        'Modern F1 gearboxes shift extremely quickly through paddle inputs and controlled hydraulic/electronic systems.',
        'Gearbox component rules can create penalties if parts are changed outside allowed limits.',
      ],
      routeLiveDataWhen: ['specific gearbox failure', 'driver penalty', 'gear ratio data'],
    },
  },
  {
    id: 'reliability-management',
    title: 'Reliability Management',
    category: 'race-strategy',
    sourceRefs: ['f1Glossary', 'fiaRegulations'],
    tags: ['reliability', 'temperatures', 'lift and coast', 'engine saving', 'brake saving', 'component life'],
    queries: ['why drivers manage the car', 'what is reliability management', 'why lift and coast', 'why save brakes'],
    answer: {
      summary: 'Reliability management means protecting components, temperatures, fuel, brakes, tyres, and power-unit life while still racing quickly.',
      keyPoints: [
        'Drivers may lift and coast, cool brakes, avoid kerbs, manage shifts, or reduce deployment to protect the car.',
        'Traffic and hot weather make cooling harder because airflow is disturbed and speeds may be lower.',
        'Component limits mean a short-term push can have season-long consequences.',
      ],
      routeLiveDataWhen: ['specific reliability warning', 'team radio instruction', 'retirement cause'],
    },
  },
  {
    id: 'track-types',
    title: 'Circuit Types',
    category: 'f1-basics',
    sourceRefs: ['f1WhatIsF1', 'f1Glossary'],
    tags: ['street circuit', 'permanent circuit', 'high downforce', 'low downforce', 'track characteristics'],
    queries: ['types of f1 tracks', 'what is street circuit', 'what is high downforce track', 'why tracks suit different cars'],
    answer: {
      summary: 'F1 circuits vary by layout, surface, corner speed, straight length, altitude, climate, kerb use, and overtaking difficulty, so different cars can excel at different tracks.',
      keyPoints: [
        'Street circuits often have walls close to the racing line, lower initial grip, and heavy track evolution.',
        'High-downforce tracks reward corner grip; low-downforce tracks reward efficiency and straight-line speed.',
        'Bumps, kerbs, altitude, and temperature can change setup priorities dramatically.',
      ],
      routeLiveDataWhen: ['specific circuit forecast', 'team performance at current event'],
    },
  },
  {
    id: 'altitude-effects',
    title: 'Altitude Effects',
    category: 'car-technology',
    sourceRefs: ['f1Glossary'],
    tags: ['altitude', 'thin air', 'mexico city', 'cooling', 'downforce', 'turbo'],
    queries: ['how altitude affects f1 cars', 'why mexico is unique', 'what thin air does to f1 cars'],
    answer: {
      summary: 'High altitude means thinner air, which changes aerodynamics, cooling, and power-unit behavior.',
      keyPoints: [
        'Thinner air reduces drag but also reduces downforce and cooling mass flow.',
        'Turbocharged engines handle altitude better than naturally aspirated engines but still face thermal and compressor demands.',
        'Teams may run high-downforce bodywork yet still see high straight-line speeds because drag is lower.',
      ],
      routeLiveDataWhen: ['specific event cooling issue', 'speed trap comparison'],
    },
  },
  {
    id: 'weather-effects',
    title: 'Weather Effects',
    category: 'race-strategy',
    sourceRefs: ['f1Glossary'],
    tags: ['weather', 'rain', 'wind', 'track temperature', 'ambient temperature', 'strategy'],
    queries: ['how weather affects f1', 'why track temperature matters', 'how wind affects f1 cars', 'why rain changes strategy'],
    answer: {
      summary: 'Weather changes tyre temperature, cooling, aero balance, braking, visibility, and strategy.',
      keyPoints: [
        'Track temperature strongly affects tyre warm-up, degradation, and compound choice.',
        'Wind can destabilize cars because aerodynamic loads depend on airflow direction and speed.',
        'Rain changes tyre choice, braking points, visibility, Safety Car risk, and race-control decisions.',
      ],
      routeLiveDataWhen: ['current weather', 'race forecast', 'live tyre choice'],
    },
  },
  {
    id: 'stewards-evidence',
    title: 'Stewards Evidence',
    category: 'sporting-rules',
    sourceRefs: ['fiaRegulations'],
    tags: ['stewards evidence', 'onboard', 'telemetry', 'radio', 'hearing', 'investigation'],
    queries: ['what evidence stewards use', 'how stewards decide penalties', 'what happens in stewards hearing'],
    answer: {
      summary: 'Stewards can review video, onboard footage, timing, telemetry, team radio, marshal reports, documents, and driver/team statements when judging incidents.',
      keyPoints: [
        'They apply FIA rules and event notes to the specific facts of an incident.',
        'A hearing can involve team representatives and drivers explaining their position.',
        'Published decisions usually summarize the rule basis, evidence, and penalty or no-further-action outcome.',
      ],
      routeLiveDataWhen: ['specific incident evidence', 'stewards document', 'current investigation'],
    },
  },
  {
    id: 'fia-fom-roles',
    title: 'FIA and Formula 1 Roles',
    category: 'f1-basics',
    sourceRefs: ['f1WhatIsF1', 'fiaRegulations'],
    tags: ['fia', 'fom', 'formula one management', 'commercial rights', 'regulator', 'race control'],
    queries: ['difference between fia and f1', 'what does fia do', 'what does fom do', 'who runs formula 1'],
    answer: {
      summary: 'The FIA is the governing body that writes and enforces sporting and technical rules, while Formula 1/FOM handles the commercial promotion and operation of the championship.',
      keyPoints: [
        'The FIA oversees regulation, safety, licensing, officials, and sporting governance.',
        'Formula 1 manages commercial rights, broadcast, promotion, event relationships, and the fan product.',
        'Teams compete under FIA regulations inside the championship promoted commercially by Formula 1.',
      ],
    },
  },
  {
    id: 'testing-limits',
    title: 'Testing Limits',
    category: 'sporting-rules',
    sourceRefs: ['fiaRegulations'],
    tags: ['testing', 'private testing', 'pre-season testing', 'filming day', 'demo run'],
    queries: ['why f1 teams cannot test all the time', 'what is filming day', 'what is pre season testing', 'testing restrictions'],
    answer: {
      summary: 'F1 restricts testing to control costs and preserve competitive fairness, with specific rules for pre-season tests, in-season running, older cars, filming days, and demos.',
      keyPoints: [
        'Modern teams rely heavily on simulators, dynos, CFD, and wind tunnels because track testing is limited.',
        'Filming or promotional running has mileage, tyre, and purpose restrictions.',
        'Testing rules change by season and are defined in the sporting regulations.',
      ],
      routeLiveDataWhen: ['specific test schedule', 'team filming day', 'current testing allowance'],
    },
  },
  {
    id: 'tyre-allocation',
    title: 'Tyre Allocation',
    category: 'tyres-strategy',
    sourceRefs: ['fiaRegulations', 'f1Glossary'],
    tags: ['tyre allocation', 'compound allocation', 'sets', 'pirelli', 'mandatory tyres'],
    queries: ['how tyre allocation works', 'how many tyre sets', 'who chooses tyre compounds', 'what is compound allocation'],
    answer: {
      summary: 'Tyre allocation defines which compounds and how many sets are available to each driver for a weekend, under supplier and FIA rules.',
      keyPoints: [
        'The tyre supplier selects event compounds from its range, then teams manage limited sets across practice, qualifying, Sprint, and race sessions.',
        'Saving or using a set in one session affects options later in the weekend.',
        'Rules can specify mandatory race compounds or return schedules depending on format.',
      ],
      routeLiveDataWhen: ['specific weekend allocation', 'driver remaining sets', 'race tyre availability'],
    },
  },
  {
    id: 'wet-tyre-crossover',
    title: 'Wet Tyre Crossover',
    category: 'tyres-strategy',
    sourceRefs: ['f1Glossary'],
    tags: ['crossover', 'slicks', 'intermediates', 'full wets', 'drying line', 'rain strategy'],
    queries: ['what is crossover point', 'when switch from inters to slicks', 'when use full wets instead of inters', 'what is drying line'],
    answer: {
      summary: 'The crossover point is when one tyre type becomes faster than another as track wetness, temperature, and grip change.',
      keyPoints: [
        'A drying track often moves from full wets to intermediates to slicks, but timing is risky.',
        'Slicks can be much faster on a dry line but dangerous on wet patches.',
        'The first driver to switch can gain massively or lose the race if conditions are not ready.',
      ],
      routeLiveDataWhen: ['current rain radar', 'live lap times by compound', 'specific race tyre call'],
    },
  },
  {
    id: 'ers-clipping',
    title: 'ERS Clipping',
    category: 'car-technology',
    sourceRefs: ['f1Glossary'],
    tags: ['clipping', 'ers clipping', 'battery deployment', 'straight line speed', 'energy limit'],
    queries: ['what is ers clipping', 'what is clipping', 'why does ers clip', 'what is battery clipping', 'why car stops accelerating on straight'],
    answer: {
      summary: 'ERS clipping is when electrical deployment reduces or ends before the end of a straight because the allowed or available energy has been used.',
      keyPoints: [
        'Clipping can hurt top-end acceleration and make a car vulnerable before braking.',
        'Teams tune deployment to balance early acceleration, overtaking, defending, harvesting, and lap-time targets.',
        'Clipping behavior depends on rules era, circuit layout, battery state, and power-unit strategy.',
      ],
      routeLiveDataWhen: ['specific speed trace', 'driver battery state', 'telemetry comparison'],
    },
  },
  {
    id: 'drivability',
    title: 'Drivability',
    category: 'car-setup',
    sourceRefs: ['f1Glossary'],
    tags: ['drivability', 'throttle map', 'power delivery', 'traction', 'corner exit'],
    queries: ['what is drivability', 'why throttle map matters', 'why power delivery matters', 'what makes f1 car easy to drive'],
    answer: {
      summary: 'Drivability is how predictably and controllably the car responds to driver inputs, especially throttle, braking, steering, and gear shifts.',
      keyPoints: [
        'A car with good drivability lets the driver attack consistently without sudden snaps or traction loss.',
        'Power delivery, differential maps, engine braking, suspension, and tyre state all affect drivability.',
        'A theoretically fast setup can be slower if the driver cannot trust it over a stint.',
      ],
    },
  },
  {
    id: 'engine-braking',
    title: 'Engine Braking',
    category: 'car-setup',
    sourceRefs: ['f1Glossary'],
    tags: ['engine braking', 'braking stability', 'corner entry', 'rear locking', 'rotation'],
    queries: ['what is engine braking', 'how engine braking affects f1 car', 'why change engine braking'],
    answer: {
      summary: 'Engine braking is deceleration from the power unit and drivetrain when the driver lifts or brakes, and it affects corner-entry balance.',
      keyPoints: [
        'More engine braking can help slow and rotate the car but may destabilize the rear axle.',
        'Less engine braking can improve stability but may reduce rotation or change brake feel.',
        'Drivers and engineers tune engine braking together with brake balance, differential, and tyre condition.',
      ],
    },
  },
  {
    id: 'track-limits-white-line',
    title: 'White Line Track Limit',
    category: 'sporting-rules',
    sourceRefs: ['fiaRegulations'],
    tags: ['white line', 'track limits', 'kerb', 'lap deletion', 'four wheels off'],
    queries: ['is kerb part of track limits', 'what is white line rule', 'why lap deleted over white line', 'four wheels off track'],
    answer: {
      summary: 'Track limits are usually judged by whether the car remains within the defined track boundary, commonly the white lines unless event notes say otherwise.',
      keyPoints: [
        'Kerbs may be usable physically but not always count as track for limits purposes.',
        'In qualifying, a breach can delete the lap time and sometimes affect the next lap.',
        'In races, repeated breaches can lead to warnings and penalties.',
      ],
      routeLiveDataWhen: ['specific deleted lap', 'driver warning count', 'event-specific track-limit note'],
    },
  },
  {
    id: 'yellow-flag-rules',
    title: 'Yellow Flag Rules',
    category: 'sporting-rules',
    sourceRefs: ['fiaRegulations', 'f1Glossary'],
    tags: ['yellow flag', 'double yellow', 'slow down', 'no overtaking', 'marshal sector'],
    queries: ['what does yellow flag mean', 'difference single and double yellow', 'can drivers overtake under yellow', 'why slow for yellow flag'],
    answer: {
      summary: 'Yellow flags warn drivers of danger and require them to slow, be prepared to change direction, and avoid overtaking in the affected area.',
      keyPoints: [
        'A double yellow indicates a more serious hazard and stronger caution requirement.',
        'Ignoring yellow flags can create major safety risks and lead to penalties.',
        'Exact expectations can depend on marshal sector, Race Control instructions, and session context.',
      ],
      routeLiveDataWhen: ['specific yellow flag incident', 'lap time deletion under yellow', 'race-control message'],
    },
  },
  {
    id: 'black-orange-flag',
    title: 'Black and Orange Flag',
    category: 'sporting-rules',
    sourceRefs: ['f1Glossary', 'fiaRegulations'],
    tags: ['black and orange flag', 'meatball flag', 'damage', 'unsafe car', 'pit for repairs'],
    queries: ['what is black and orange flag', 'what is meatball flag', 'why driver forced to pit for damage'],
    answer: {
      summary: 'The black-and-orange flag tells a driver that their car has a mechanical problem or damage considered dangerous and that they must come to the pits.',
      keyPoints: [
        'It is commonly nicknamed the meatball flag.',
        'It can be used when loose bodywork, damage, or a technical issue creates risk to the driver or others.',
        'Race Control and officials decide when the car must be repaired or retired.',
      ],
      routeLiveDataWhen: ['specific damage call', 'race-control flag message'],
    },
  },
  {
    id: 'double-stack-pitstop',
    title: 'Double Stack Pit Stop',
    category: 'race-strategy',
    sourceRefs: ['f1Glossary'],
    tags: ['double stack', 'pit stop', 'strategy', 'safety car', 'team mates'],
    queries: ['what is double stack', 'why double stack pit stop', 'why second car waits in pits'],
    answer: {
      summary: 'A double stack is when both team cars pit on the same lap, one immediately after the other.',
      keyPoints: [
        'It often happens under Safety Car or sudden weather changes when pitting both cars is strategically urgent.',
        'The second car can lose time waiting if the first stop is not finished.',
        'Teams double stack when the cost of not pitting is expected to be worse than the pit-lane delay.',
      ],
      routeLiveDataWhen: ['specific double stack', 'team pit stop timing'],
    },
  },
  {
    id: 'team-orders',
    title: 'Team Orders',
    category: 'race-strategy',
    sourceRefs: ['f1Glossary', 'fiaRegulations'],
    tags: ['team orders', 'swap positions', 'let through', 'team strategy', 'drivers championship'],
    queries: ['what are team orders', 'are team orders allowed', 'why teams swap drivers', 'why driver told to let teammate pass'],
    answer: {
      summary: 'Team orders are instructions that prioritize the team’s strategic or championship interests, such as swapping cars, holding position, or helping a teammate.',
      keyPoints: [
        'Teams may use orders to maximize constructors points, protect tyre strategy, or support a championship contender.',
        'They can be controversial because they affect sporting competition between teammates.',
        'Rules and attitudes around team orders have changed historically.',
      ],
      routeLiveDataWhen: ['specific radio instruction', 'team order controversy', 'championship scenario'],
    },
  },
  {
    id: 'constructor-championship-importance',
    title: 'Why Constructors Championship Matters',
    category: 'championship',
    sourceRefs: ['f1WhatIsF1'],
    tags: ['constructors championship', 'prize money', 'team points', 'development', 'prestige'],
    queries: ['why constructors championship matters', 'why teams care about constructors', 'is constructors more important than drivers'],
    answer: {
      summary: 'The constructors championship matters because it measures team performance, carries prestige, influences commercial value, and can affect prize-money distribution.',
      keyPoints: [
        'Both drivers score points for the same constructor, so consistency across two cars matters.',
        'A strong constructors result supports sponsors, recruitment, morale, and long-term competitiveness.',
        'Drivers often receive more public attention, but teams are built around winning the constructors title.',
      ],
      routeLiveDataWhen: ['current prize money', 'current constructors standings'],
    },
  },
  {
    id: 'f1-points-abbreviations',
    title: 'Common F1 Abbreviations',
    category: 'glossary',
    sourceRefs: ['f1Glossary'],
    tags: ['abbreviations', 'dnf', 'dns', 'dsq', 'fp1', 'q1', 'vsc', 'sc', 'drs', 'ers'],
    queries: ['common f1 abbreviations', 'what does dnf dns dsq mean', 'what does sc vsc drs ers mean'],
    answer: {
      summary: 'F1 uses many abbreviations for sessions, systems, and race status: FP for practice, Q for qualifying, DNF/DNS/DSQ for result status, SC/VSC for neutralizations, and DRS/ERS for car systems.',
      keyPoints: [
        'FP1, FP2, and FP3 are free practice sessions; Q1, Q2, and Q3 are qualifying segments.',
        'DNF means did not finish, DNS did not start, and DSQ disqualified.',
        'SC means Safety Car, VSC Virtual Safety Car, DRS Drag Reduction System, and ERS Energy Recovery System.',
      ],
    },
  },
  {
    id: 'f1-car-numbers',
    title: 'Driver Numbers',
    category: 'f1-basics',
    sourceRefs: ['fiaRegulations', 'f1WhatIsF1'],
    tags: ['driver number', 'car number', 'number 1', 'champion number', 'permanent number'],
    queries: ['how f1 driver numbers work', 'why driver uses number 1', 'can drivers choose numbers', 'what are permanent numbers'],
    answer: {
      summary: 'Modern F1 drivers use permanent race numbers, while the reigning world champion may choose to use number 1.',
      keyPoints: [
        'Driver numbers help identify cars across sessions and seasons.',
        'Number selection and availability follow FIA rules.',
        'The champion using number 1 is optional in modern F1.',
      ],
      routeLiveDataWhen: ['current driver number list', 'new rookie number choice'],
    },
  },
  {
    id: 'car-launches-liveries',
    title: 'Car Launches and Liveries',
    category: 'f1-basics',
    sourceRefs: ['f1WhatIsF1'],
    tags: ['car launch', 'livery', 'shakedown', 'filming day', 'sponsor'],
    queries: ['what is f1 car launch', 'what is livery', 'why teams launch cars', 'what is shakedown'],
    answer: {
      summary: 'A car launch reveals a team’s new season identity, livery, sponsors, and sometimes the actual car or a show car before testing begins.',
      keyPoints: [
        'A livery is the car’s paint and sponsor design.',
        'Teams may hide technical details at launches to avoid revealing development secrets.',
        'A shakedown or filming day can check basic systems before full running, within testing rules.',
      ],
      routeLiveDataWhen: ['specific launch date', 'new livery reveal', 'current sponsor change'],
    },
  },
  {
    id: 'f1-academy-junior-ladder',
    title: 'Junior Ladder to F1',
    category: 'f1-basics',
    sourceRefs: ['f1WhatIsF1', 'fiaRegulations'],
    tags: ['junior ladder', 'f2', 'f3', 'f1 academy', 'super licence', 'karting'],
    queries: ['how drivers get to f1', 'what is f2 and f3', 'what is junior ladder', 'what is f1 academy'],
    answer: {
      summary: 'Drivers usually reach F1 through karting and junior single-seater categories, building experience and Super Licence eligibility before earning an F1 seat.',
      keyPoints: [
        'Formula 2 and Formula 3 are major steps on the FIA single-seater pathway.',
        'Driver academies support selected young drivers with funding, coaching, simulator work, and testing opportunities.',
        'F1 Academy is a women-focused series designed to develop and showcase talent in the pathway.',
      ],
      routeLiveDataWhen: ['current junior standings', 'driver academy roster', 'specific Super Licence points'],
    },
  },
  {
    id: 'commercial-rights-calendar',
    title: 'Calendar and Commercial Rights',
    category: 'f1-basics',
    sourceRefs: ['f1WhatIsF1'],
    tags: ['calendar', 'grand prix contract', 'commercial rights', 'promoter', 'host race'],
    queries: ['how f1 calendar is made', 'why races join or leave calendar', 'what is race promoter', 'who decides f1 calendar'],
    answer: {
      summary: 'The F1 calendar is shaped by commercial agreements, logistics, FIA approval, circuit readiness, regional strategy, and team travel demands.',
      keyPoints: [
        'Race promoters host events through commercial arrangements with Formula 1.',
        'The FIA role includes governance and safety approval through the international calendar process.',
        'Calendar design balances markets, history, logistics, climate, and championship flow.',
      ],
      routeLiveDataWhen: ['current calendar', 'future race contract', 'event cancellation'],
    },
  },
];

export const oracleKnowledgeIndex = oracleKnowledgeBase.reduce((index, entry) => {
  for (const tag of entry.tags) {
    const key = tag.toLowerCase();
    index[key] = index[key] || [];
    index[key].push(entry.id);
  }
  return index;
}, Object.create(null));
